"use client"

import { useRef, type KeyboardEvent } from "react"
import { ArrowReturnIcon } from "@/components/ui/icons"

export type StarterPromptsVariant = "starter" | "followup"

export interface StarterPromptsProps {
  prompts: string[]
  onSelect: (prompt: string, index: number) => void
  /**
   * `"starter"` (default) — the pill/button row shown before the first user
   * turn. `"followup"` — the compact, full-width suggestion rows shown under
   * the latest bot answer: a small heading, a leading ↳ affordance, hairline
   * dividers, and a gentle enter animation.
   */
  variant?: StarterPromptsVariant
  /**
   * Sentence-case heading rendered above the rows in the `"followup"` variant.
   * Ignored by `"starter"`. Also becomes the listbox's accessible name so the
   * visible heading isn't announced twice.
   */
  heading?: string
}

// The follow-up variant stays compact inside the floating panel: at most three
// suggestions so the rows never dominate the answer they sit beneath.
const MAX_FOLLOWUP_ROWS = 3

export function StarterPrompts({
  prompts,
  onSelect,
  variant = "starter",
  heading,
}: StarterPromptsProps) {
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([])

  const items =
    variant === "followup" ? prompts.slice(0, MAX_FOLLOWUP_ROWS) : prompts

  if (items.length === 0) return null

  function moveFocus(from: number, delta: 1 | -1): void {
    const next = (from + delta + items.length) % items.length
    buttonsRef.current[next]?.focus()
  }

  function onKey(e: KeyboardEvent<HTMLButtonElement>, i: number): void {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      moveFocus(i, 1)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      moveFocus(i, -1)
    } else if (e.key === "Home") {
      e.preventDefault()
      buttonsRef.current[0]?.focus()
    } else if (e.key === "End") {
      e.preventDefault()
      buttonsRef.current[items.length - 1]?.focus()
    }
  }

  const registerRef =
    (i: number) =>
    (el: HTMLButtonElement | null): void => {
      buttonsRef.current[i] = el
    }

  if (variant === "followup") {
    return (
      <div className="flex flex-col px-3 pt-1 pb-3">
        {heading && (
          <div
            aria-hidden="true"
            className="followup-enter px-1 pt-1 pb-1 text-xs font-medium text-neutral-500"
          >
            {heading}
          </div>
        )}
        <div
          role="listbox"
          aria-label={heading ?? "Suggested follow-up questions"}
        >
          {items.map((p, i) => (
            <button
              key={`${i}-${p}`}
              type="button"
              role="option"
              aria-selected="false"
              ref={registerRef(i)}
              onClick={() => onSelect(p, i)}
              onKeyDown={(e) => onKey(e, i)}
              style={{ animationDelay: `${i * 45}ms` }}
              className="followup-enter text-foreground focus:ring-deep-blue-50 hover:text-foreground flex w-full cursor-pointer items-start gap-2 rounded-lg border-t border-neutral-100 px-1 py-2 text-left text-sm text-neutral-700 transition-colors outline-none first:border-t-0 hover:bg-neutral-50 focus:ring-2"
            >
              <ArrowReturnIcon
                size={16}
                className="mt-0.5 shrink-0 text-neutral-400"
              />
              <span>{p}</span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      role="listbox"
      aria-label="Suggested questions"
      className="flex flex-col gap-2 p-3"
    >
      {items.map((p, i) => (
        <button
          key={`${i}-${p}`}
          type="button"
          role="option"
          aria-selected="false"
          ref={registerRef(i)}
          onClick={() => onSelect(p, i)}
          onKeyDown={(e) => onKey(e, i)}
          className="text-foreground hover:border-deep-blue-200 hover:bg-deep-blue-50 focus:border-deep-blue-800 focus:ring-deep-blue-50 cursor-pointer rounded-[12px] border border-neutral-200 bg-white px-3 py-2 text-left text-sm transition-colors outline-none focus:ring-2 active:scale-[0.98]"
        >
          {p}
        </button>
      ))}
    </div>
  )
}
